import React, {useEffect, useState} from 'react';
import {shape, func} from 'prop-types';
import {OPTION_TYPE, renderOptionFilter, OptionFilterAddDialogue, createContentfulOptionFilterString} from './optionFilter';
import {createContentfulTextFilterString, renderTextFilter, TextFilterAddDialogue, TEXT_TYPE} from './textFilter';
import {FormGroup, Panel} from 'react-bootstrap';
import {Cross} from '../misc/Cross';
import {BooleanFilterAddDialogue, BOOLEAN_TYPE, createContentfulBooleanFilterString, renderBooleanFilter} from './booleanFilter';
import {createContentfulQueryFilterString, QueryFilterAddDialogue, QUERY_TYPE, renderQueryFilter} from './queryFilter';
import './filters.scss';

const FilterContainer = ({availableFilters, allFiltersCallback}) => {
  const [showAddDialogue, setShowAddDialogue] = useState(false);
  const [selectedFilterData, setSelectedFilterData] = useState(null);
  const [selectedFilterValue, setSelectedFilterValue] = useState('');
  const [filters, setFilters] = useState([]);

  const onChangeSelection = (e) => {
    const fieldName = e.target.value;

    setSelectedFilterValue(fieldName);
    setSelectedFilterData(availableFilters[fieldName] ?? null);
  };

  const handleFilterAddCallback = (fieldName, type, options) => {
    const existingFilters = Array.from(filters);
    existingFilters.push({
      ...options,
      type,
      fieldName,
      id: Date.now() // Unique id for this field
    });

    setFilters(existingFilters);

    // Clear selections
    setShowAddDialogue(false);
    setSelectedFilterData(null);
    setSelectedFilterValue('');
  };

  useEffect(() => {
    if (filters) {
      const contentfulFilters = filters.reduce((res, filter) => {
        let key, value;
        switch (filter.type) {
          case OPTION_TYPE: {
            [key, value] = createContentfulOptionFilterString(filter);
            break;
          }
          case TEXT_TYPE: {
            [key, value] = createContentfulTextFilterString(filter);
            break;
          }
          case BOOLEAN_TYPE: {
            [key, value] = createContentfulBooleanFilterString(filter);
            break;
          }
          case QUERY_TYPE: {
            [key, value] = createContentfulQueryFilterString(filter);
            break;
          }
          default:
            return res;
        }

        if (res[key]) {
          res[key] += `,${value}`;
        } else {
          res[key] = value;
        }

        return res;
      }, {});

      allFiltersCallback(contentfulFilters);
    }
  }, [filters]);


  const handleRemoveFilterCallback = (id) => {
    const updatedFilters = filters.filter((filter) => filter.id !== id);
    setFilters(updatedFilters);
  };

  const handleEditFilterCallback = (id, fieldName, type, options) => {
    const updatedFilters = Array.from(filters);
    const filterIndex = updatedFilters.findIndex((filter) => filter.id === id);

    if (filterIndex !== -1) {
      updatedFilters[filterIndex] = {
        ...options,
        type,
        fieldName,
        id
      };
    }

    setFilters(updatedFilters);
  };

  const renderFilterAddDialogue = (displayName, filter = {}) => {
    if (filter.multiuseOperators && filters.length > 0) {
      const existingSameFilter = filters.find((f) => f.fieldName === filter.fieldName);
      if (existingSameFilter) {
        filter = {
          ...filter,
          supportedOperators: filter.multiuseOperators
        };
      }
    }

    switch (filter.type) {
      case OPTION_TYPE:
        return <OptionFilterAddDialogue displayName={displayName} filterCallback={handleFilterAddCallback} filterOptions={filter} />;
      case TEXT_TYPE:
        return <TextFilterAddDialogue displayName={displayName} filterCallback={handleFilterAddCallback} filterOptions={filter} />;
      case BOOLEAN_TYPE:
        return <BooleanFilterAddDialogue displayName={displayName} filterCallback={handleFilterAddCallback} filterOptions={filter} />;
      case QUERY_TYPE:
        return <QueryFilterAddDialogue filterCallback={handleFilterAddCallback} filterOptions={filter} />;
      default:
        return null;
    }
  };

  const renderAddDialogue = (availableFilters) => {
    if (!availableFilters || Object.keys(availableFilters).length === 0) {
      return null;
    }

    return (
      <>
        <div id="add-filter-backdrop" className="invisible-backdrop" onClick={() => setShowAddDialogue(false)} />
        <Panel id="add-filter" defaultExpanded={showAddDialogue} style={{
          position: 'absolute',
          minWidth: '200px'
        }}>
          <Panel.Collapse>
            <Panel.Body style={{padding: 8}}>
              <div className='filter-panel-body'>
                <div>
                  <label style={{display: 'flex', justifyContent: 'space-between'}}>
                    <b>Legg til</b>
                    <a href="#" role="button" style={{padding: '0 5px'}} onClick={(e) => {
                      e.preventDefault();
                      setShowAddDialogue(false);
                    }}>
                      <Cross />
                    </a>
                  </label>
                </div>
                <FormGroup>
                  <select className="form-control" value={selectedFilterValue} onChange={onChangeSelection}>
                    <option value="">Velg felt</option>
                    {filteredAvailableFilters.map(([name]) => (
                      <option key={`available-filter-${name}`} value={name}>{name}</option>
                    ))}
                  </select>
                </FormGroup>
                {selectedFilterData !== null && renderFilterAddDialogue(selectedFilterValue, selectedFilterData)}
              </div>
            </Panel.Body>
          </Panel.Collapse>
        </Panel>
      </>
    );
  };

  const filteredAvailableFilters = Object.entries(availableFilters).filter(([, availableFilter]) => {
    for (let i = 0; i < filters.length; i++) {
      if (filters[i].fieldName === availableFilter.fieldName) {
        if (!availableFilter.multiuseOperators) {
          // Filter already added, and do not support multi use
          return false;
        }
      }
    }

    return true;
  });

  return (
    <>
      <div className='filter-container'>
        <div style={{
          marginBottom: 10
        }}>
          <b>FILTER</b>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {filters.map((filter) => {
            switch (filter.type) {
              case OPTION_TYPE:
                return renderOptionFilter(`filter-${filter.fieldName}-${filter.id}`, filter, handleRemoveFilterCallback, handleEditFilterCallback);
              case TEXT_TYPE:
                return renderTextFilter(`filter-${filter.fieldName}-${filter.id}`, filter, handleRemoveFilterCallback, handleEditFilterCallback);
              case BOOLEAN_TYPE:
                return renderBooleanFilter(`filter-${filter.fieldName}-${filter.id}`, filter, handleRemoveFilterCallback, handleEditFilterCallback);
              case QUERY_TYPE:
                return renderQueryFilter(`filter-${filter.fieldName}-${filter.id}`, filter, handleRemoveFilterCallback, handleEditFilterCallback);
              default:
                return (
                  <p>Invalid filter type found! Data: {JSON.stringify(filter)}</p>
                );
            }
          })}
          {filteredAvailableFilters.length > 0 && (
            <div>
              <a href="#" role="button" className='add-filter-button' onClick={(e) => {
                e.preventDefault();
                setShowAddDialogue(true);
              }}>Legg til</a>
              {showAddDialogue && renderAddDialogue(filteredAvailableFilters)}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

FilterContainer.propTypes = {
  availableFilters: shape({}).isRequired,
  allFiltersCallback: func.isRequired
};
FilterContainer.defaultProps = {};

export default FilterContainer;
